import {
  Checkbox,
  CircularProgress,
  InputAdornment,
  TextField,
} from "@mui/material";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import classNames from "classnames";
import { X as XIcon } from "lucide-react";
import * as React from "react";

import { useDebouncedCallback } from "@/shared/hooks/useDebouncedCallback";

import {
  type FilterOption,
  type FilterOptions,
  type FilterOptionsResolver,
  type LabeledOption,
  type ResolvedOptions,
} from "../FilterDefinition";
import {
  type EnumComparator,
  type SetComparator,
  isValueless,
} from "../FilterSchema";

import styles from "./OptionsValueInput.module.css";

// Trailing debounce applied to the search input before a server-searched
// resolver is re-invoked, so a burst of typing collapses to one query. Matches
// the convention used by other server-search inputs (e.g. `OrgAutocomplete`).
const SEARCH_DEBOUNCE_MS = 300;

// Shortest search term worth sending to a server resolver. Postgres can
// extract no trigram from a 1-2 character `%term%`, so those searches
// degenerate to a full index scan — slower than having no index at all. The
// empty search stays allowed: it is the intentional first-page-on-open query.
const MIN_SERVER_SEARCH_LENGTH = 3;

// Fixed height (px) of one option row. Rows are single-line — the label
// truncates with an ellipsis (see `.row-label`) — so a constant estimate is
// exact and the virtualizer never needs to re-measure. This constant is the
// single source of the row height: each row's inline `height` style derives
// from it (the stylesheet declares none).
const OPTION_ROW_HEIGHT = 32;

// Rows rendered outside the viewport on each side, trading a little extra DOM
// for scroll headroom so fast scrolling doesn't flash blank rows.
const OPTION_OVERSCAN = 8;

interface OptionsValueInputBaseProps {
  comparator: SetComparator | EnumComparator;
  /**
   * The selected options — wire ``value`` plus the display ``label`` the user
   * saw when picking each one. Carrying the label in the selection is what
   * lets chips and pinned rows render labels for server-searched options
   * whose full option list isn't available later.
   */
  value: LabeledOption[];
  /** Fired on every selection change with the next selected options. */
  onChange: (next: LabeledOption[]) => void;
}

/**
 * ``options`` in either form, with ``optionsKey`` REQUIRED alongside a
 * resolver and forbidden alongside a static array.
 *
 * A resolver's results are fetched, so the query needs a name for WHICH source
 * produced them. The picker cannot derive that itself: a caller may rebuild an
 * equivalent resolver on every render (an inline arrow, say), so the function
 * reference is worthless as an identity. The caller names the source instead,
 * and the type system makes forgetting to a compile error.
 *
 * Callers switching between genuinely different sources should ALSO remount the
 * picker (``key``) so its search text and latched option set reset — see the
 * identity branch of ``FilterForm``. ``optionsKey`` then keeps the two sources'
 * query entries distinct across that remount.
 *
 * A static array needs none of this — it is re-derived from props on every
 * render — so passing a key there is rejected rather than quietly ignored.
 */
type OptionsSourceProps =
  | {
      /** Statically known to be an in-memory array, so no key is needed. */
      options: string[] | LabeledOption[];
      optionsKey?: never;
    }
  | {
      /**
       * Possibly (or definitely) a resolver. Callers holding an un-narrowed
       * ``FilterOptions`` land here — which is why this branch accepts arrays
       * too, rather than forcing every generic call site to narrow before it
       * can render. A key supplied for what turns out to be a static array is
       * simply unused.
       */
      options: FilterOptions;
      /**
       * Names WHICH option source ``options`` currently is, for the query key.
       * Change it when the resolver starts representing something else (a
       * different principal kind, a different field); keep it fixed when the
       * same source is merely rebuilt. A local discriminator like the principal
       * kind or the field name is sufficient.
       */
      optionsKey: string;
    };

type OptionsValueInputProps = OptionsValueInputBaseProps & OptionsSourceProps;

function normalizeOption(option: FilterOption): LabeledOption {
  return typeof option === "string" ? { label: option, value: option } : option;
}

/**
 * Searchable, multi-select checkbox list shared by the ``set``, ``enum`` and
 * ``identity`` filter types. Valueless comparators (``IS_NULL`` / ``IS_NOT_NULL``) render
 * nothing — redundant with ``FilterForm``, which already hides the value row
 * for them.
 *
 * ``options`` drives one of two modes:
 *
 * - **Static array** (``string[]`` or ``LabeledOption[]``): the whole list is
 *   held in memory and filtered CLIENT-SIDE by label substring as the user
 *   types. No async, no debounce.
 * - **Server-searched resolver** (function form, see
 *   {@link FilterOptionsResolver}): the resolver is invoked with the empty
 *   search on open, and that first page decides what happens next. If it was
 *   complete, the picker keeps it and filters it client-side like a static
 *   array. If it was ``truncated``, the picker re-queries with the DEBOUNCED
 *   (300 ms) search text on each keystroke and renders results
 *   SERVER-AUTHORITATIVELY — in the returned order, with NO client-side
 *   filtering. See {@link useServerOptions}. Either way a loading hint shows
 *   while a search is in flight without blanking the previously-rendered
 *   options (``keepPreviousData``), and selected values absent from the
 *   current results are pinned at the top so they stay visible and
 *   deselectable.
 *
 * Both modes report selection changes through ``onChange`` as labeled
 * options, so every selected value carries its own display label.
 */
export function OptionsValueInput({
  comparator,
  value,
  onChange,
  options,
  optionsKey,
}: OptionsValueInputProps) {
  const [searchValue, setSearchValue] = React.useState("");
  // The input stays immediate; the server path searches the settled term.
  // Trimmed so a trailing space can't turn an exact match into `%acme %`.
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const debounceSearch = useDebouncedCallback(
    setDebouncedSearch,
    SEARCH_DEBOUNCE_MS,
  );
  // Threaded down to whichever list variant is active, all the way to the
  // `role="listbox"` element itself, so ArrowDown can focus it directly
  // instead of guessing at its DOM position via `querySelector`.
  const listboxRef = React.useRef<HTMLDivElement>(null);

  if (isValueless(comparator)) {
    return null;
  }

  const handleSearchChange = (next: string) => {
    setSearchValue(next);
    debounceSearch(next.trim());
  };

  return (
    <div className={styles.input}>
      {typeof options === "function" ? (
        <ServerOptionList
          resolver={options}
          // Non-null by construction: the props union requires `optionsKey`
          // exactly when `options` is a resolver, which this branch established.
          optionsKey={optionsKey!}
          search={debouncedSearch}
          searchValue={searchValue}
          onSearchChange={handleSearchChange}
          value={value}
          onChange={onChange}
          listboxRef={listboxRef}
        />
      ) : (
        <StaticOptionList
          options={options}
          searchValue={searchValue}
          onSearchChange={handleSearchChange}
          value={value}
          onChange={onChange}
          listboxRef={listboxRef}
        />
      )}
    </div>
  );
}

/**
 * What a list variant feeds the shared ``OptionList``: the current result set
 * (in render order) plus its state flags. Splitting by SOURCE — rather than
 * having each variant assemble its own rows — is what guarantees pinning and
 * the loading/error/empty/truncation branches exist on both paths.
 */
interface OptionSource {
  options: LabeledOption[];
  isLoading: boolean;
  isError: boolean;
  truncated: boolean;
}

/**
 * Static, in-memory option source — filtered CLIENT-SIDE by label substring,
 * synchronously (which is why the array isn't just wrapped in a resolver).
 * Search matches ``label`` (what the user sees), never the opaque wire
 * ``value``. Consumers must keep the set small enough to hold in memory.
 */
function useStaticOptions(
  options: string[] | LabeledOption[],
  searchValue: string,
): OptionSource {
  const normalized = React.useMemo(
    () => (options as FilterOption[]).map(normalizeOption),
    [options],
  );
  const filtered = React.useMemo(
    () => filterByLabel(normalized, searchValue),
    [normalized, searchValue],
  );

  return React.useMemo(
    () => ({
      options: filtered,
      isLoading: false,
      isError: false,
      truncated: false,
    }),
    [filtered],
  );
}

/**
 * Server-searched option source, which downgrades to client-side filtering as
 * soon as it can prove that's equivalent.
 *
 * On open it queries the resolver with the empty search. What comes back
 * decides the mode for the life of the mount — pointing the picker at a
 * different source means remounting it (see ``OptionsSourceProps``), which
 * re-runs the decision from scratch:
 *
 * - **Complete** (``truncated`` falsy) — the server returned the ENTIRE option
 *   set, so no later search can surface an option this page doesn't already
 *   hold. The set is latched and every subsequent keystroke filters it in
 *   memory: no debounce wait, no request per keystroke, no loading flicker.
 * - **Truncated** — the server capped the page, so options exist that aren't on
 *   screen and only the server can find them. The picker stays server-searched:
 *   each debounced keystroke re-queries, results render verbatim with NO
 *   client-side filtering, and a hint tells the user to narrow.
 *
 * In both modes ``keepPreviousData`` holds the previous page while a request is
 * in flight, so the list never blanks mid-keystroke.
 */
function useServerOptions(
  resolver: FilterOptionsResolver,
  optionsKey: string,
  search: string,
  searchValue: string,
): OptionSource {
  // Latched to the full option set once the empty search proves the resolver
  // returned everything; `null` while the picker is still server-searched.
  const [completeOptions, setCompleteOptions] = React.useState<
    FilterOption[] | null
  >(null);

  const isComplete = completeOptions !== null;

  // With the whole set in hand there is nothing left to ask the server, so the
  // query stays pinned to the empty search and never refetches on a keystroke.
  const activeSearch = isComplete ? "" : search;

  // Per-mount id folded into the query key so two concurrently-rendered
  // pickers with the same `optionsKey` (e.g. a "tags" picker each on
  // DatasetsList, EventsList, and FilesList — three different resolvers,
  // potentially three different orgs) never share a query. Without it they
  // collide on `["filter-options", optionsKey, activeSearch]`, and TanStack
  // treats the second mount as an additional observer of the FIRST mount's
  // query — the second picker would render the first resolver's results, a
  // cross-resolver (and potentially cross-org) data leak. `gcTime: 0` below
  // already means nothing outlives this mount, so the extra key segment
  // costs nothing.
  const instanceId = React.useId();

  const query = useQuery({
    // Named by which source is active plus the search. Resolver IDENTITY is
    // deliberately absent: `optionsKey` is the caller's declaration of which
    // source this is, so a resolver rebuilt every render (an inline arrow, say)
    // is harmless rather than a refetch per render.
    //
    // `optionsKey` is not globally unique (it is the field name for
    // `set`/`enum`, so "tags" collides across the datasets and files pages).
    // The per-mount `instanceId` keeps concurrent mounts apart, and nothing is
    // retained across mounts — see the gcTime/staleTime note below.
    queryKey: ["filter-options", instanceId, optionsKey, activeSearch],
    queryFn: ({ signal }) => resolver(activeSearch, signal),
    // Retention is deliberately off: it belongs to the resolver, which is the
    // only layer that can see the endpoint and org behind it. Resolvers wanting
    // cross-mount sharing cache internally against the app query client with a
    // fully-qualified key — see `useAuditIdentityOptions`.
    gcTime: 0,
    staleTime: 0,
    // Hold back terms too short to search efficiently server-side; the
    // previous page stays visible via `placeholderData` until the term grows.
    enabled:
      activeSearch.length === 0 ||
      activeSearch.length >= MIN_SERVER_SEARCH_LENGTH,
    // Keep the last page visible while the next search is in flight so the list
    // doesn't blank on every keystroke.
    placeholderData: keepPreviousData,
  });

  // A resolver may return a bare list (complete, no truncation) or a
  // `{ options, truncated }` object; normalize to the object shape. Memoized
  // so the no-data fallback keeps a stable identity — `options` (and the
  // pinning effect keyed on it) must only change when the RESULTS change.
  const resolved: ResolvedOptions = React.useMemo(() => {
    if (Array.isArray(query.data)) {
      return { options: query.data, truncated: false };
    }
    return query.data ?? { options: [], truncated: false };
  }, [query.data]);

  // Latch the complete set the first time the EMPTY search returns untruncated.
  // The `isPlaceholderData` guard matters: with `keepPreviousData`, returning to
  // the empty search briefly reports the previous search's (narrower) page under
  // the empty key, and latching that would freeze the picker on a subset.
  const isCompletePage =
    query.isSuccess && !query.isPlaceholderData && !resolved.truncated;
  React.useEffect(() => {
    if (activeSearch === "" && isCompletePage) {
      setCompleteOptions(resolved.options);
    }
  }, [activeSearch, isCompletePage, resolved.options]);

  // Snapshot of the most recent SUCCESSFUL response, held in a ref rather
  // than relied upon from TanStack's own cache: each debounced search term
  // is its own queryKey (see above), so a search term that has just failed
  // is a query that has NEVER had data — there is nothing for TanStack to
  // fall back on. `placeholderData: keepPreviousData` only substitutes data
  // while the query is `pending`; once it settles into `error`, TanStack
  // stops applying it, and `query.data` reverts to `undefined`. Updating
  // this ref during render (mirroring `valueRef` below) is what actually
  // keeps the last good page on screen through that transition.
  const lastGoodRef = React.useRef<ResolvedOptions>({
    options: [],
    truncated: false,
  });
  if (query.data !== undefined) {
    lastGoodRef.current = resolved;
  }
  const effectiveResolved = query.isError ? lastGoodRef.current : resolved;

  const normalized = React.useMemo(
    () => (completeOptions ?? effectiveResolved.options).map(normalizeOption),
    [completeOptions, effectiveResolved.options],
  );

  // In complete mode, filter the latched set by label substring — the same
  // client-side match `useStaticOptions` applies. Driven by the RAW search text
  // rather than the debounced copy: there is no request to collapse, so waiting
  // out the debounce would only add lag.
  const visible = React.useMemo(
    () => (isComplete ? filterByLabel(normalized, searchValue) : normalized),
    [isComplete, normalized, searchValue],
  );

  // In complete mode the query is parked on the empty search and any refetch is
  // a background revalidation of a set already on screen — surfacing a spinner
  // for that would flicker mid-typing, when nothing is actually being fetched
  // for the user's keystroke.
  const isLoading = query.isFetching && !isComplete;

  return React.useMemo(
    () => ({
      options: visible,
      isLoading,
      isError: query.isError,
      // Exclusive with the latch by construction: a complete set is by
      // definition untruncated, so this can only be true while server-searched.
      truncated: effectiveResolved.truncated ?? false,
    }),
    [visible, isLoading, query.isError, effectiveResolved.truncated],
  );
}

/** Client-side substring match on ``label`` — what the user sees, never the wire value. */
function filterByLabel(
  options: LabeledOption[],
  searchValue: string,
): LabeledOption[] {
  const needle = searchValue.trim().toLowerCase();
  if (needle === "") {
    return options;
  }
  return options.filter((option) =>
    option.label.toLowerCase().includes(needle),
  );
}

interface OptionListProps {
  source: OptionSource;
  searchValue: string;
  onSearchChange: (next: string) => void;
  value: LabeledOption[];
  onChange: (next: LabeledOption[]) => void;
  listboxRef: React.RefObject<HTMLDivElement>;
}

/**
 * Shared row assembly and state branches for BOTH option sources: pins
 * selected-but-absent options, renders the loading / error / empty /
 * truncation states, and delegates row rendering to ``OptionListbox``.
 *
 * Owns the search input too, so the in-flight indicator can live inside it
 * rather than as a block that reflows the list (see ``SearchInput``).
 */
function OptionList({
  source,
  searchValue,
  onSearchChange,
  value,
  onChange,
  listboxRef,
}: OptionListProps) {
  // Pin selected options missing from the current result set so they stay
  // visible and deselectable after the search moves away. Each selection
  // carries its own label, so no lookup is needed.
  //
  // The pinned set is a SNAPSHOT taken when the result set changes, not a
  // per-render derivation from the live selection. Deriving it live would
  // unmount a pinned row the instant the user unchecks it — dropping keyboard
  // focus to <body> and leaving no way to undo the click short of retyping
  // the search that surfaced the row. A snapshot keeps the row mounted
  // (unchecked) until the next search moves the results on.
  const valueRef = React.useRef(value);
  valueRef.current = value;
  const [pinned, setPinned] = React.useState<LabeledOption[]>([]);
  // NUL-joined and keyed on result CONTENT, not array identity: a parent re-rendering with a
  // freshly-built (but equal) options array must not re-derive the pinned set
  // mid-toggle, or the snapshot degenerates to the live derivation it exists
  // to avoid.
  const resultsKey = React.useMemo(
    () => source.options.map((option) => option.value).join("\u0000"),
    [source.options],
  );
  React.useEffect(() => {
    const inResults = new Set(source.options.map((option) => option.value));
    setPinned(
      valueRef.current.filter((option) => !inResults.has(option.value)),
    );
    // `source.options` intentionally omitted: `resultsKey` is derived from it
    // and only changes when the option VALUES change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultsKey]);

  const rows = [...pinned, ...source.options];

  return (
    <>
      <SearchInput
        value={searchValue}
        onChange={onSearchChange}
        isLoading={source.isLoading}
        listboxRef={listboxRef}
      />
      {source.isError && <ErrorState />}
      {rows.length > 0 ? (
        <OptionListbox
          options={rows}
          value={value}
          onToggle={(option) => onChange(toggleOption(value, option))}
          listboxRef={listboxRef}
          truncated={source.truncated && !source.isError}
          resultCount={source.options.length}
        />
      ) : (
        !source.isLoading && !source.isError && <EmptyState />
      )}
    </>
  );
}

function SearchInput({
  value,
  onChange,
  isLoading,
  listboxRef,
}: {
  value: string;
  onChange: (next: string) => void;
  isLoading: boolean;
  listboxRef: React.RefObject<HTMLDivElement>;
}) {
  let adornment: React.ReactNode = null;
  if (isLoading) {
    adornment = <CircularProgress size={14} />;
  } else if (value.length > 0) {
    adornment = (
      <button
        type="button"
        className={styles.searchClear}
        onClick={() => onChange("")}
        aria-label="Clear search"
      >
        <XIcon size={14} />
      </button>
    );
  }

  return (
    <>
      <TextField
        autoComplete="off"
        size="small"
        type="search"
        className={styles.searchInput}
        placeholder="Search options..."
        value={value}
        onChange={(event) => onChange(event.target.value)}
        inputProps={{ "aria-label": "Search options" }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">{adornment}</InputAdornment>
          ),
        }}
        // The combobox affordance: ArrowDown hands focus to the option
        // listbox (a sibling rendered by whichever list variant is active),
        // whose own keyboard model takes over from there.
        onKeyDown={(event) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            listboxRef.current?.focus();
          }
        }}
      />
      {/* The spinner is decorative; this carries the same news to screen
          readers, which the removed block-level loading row used to. */}
      <span className="sr-only" role="status" aria-live="polite">
        {isLoading ? "Loading options…" : ""}
      </span>
    </>
  );
}

interface StaticOptionListProps {
  options: string[] | LabeledOption[];
  searchValue: string;
  onSearchChange: (next: string) => void;
  value: LabeledOption[];
  onChange: (next: LabeledOption[]) => void;
  listboxRef: React.RefObject<HTMLDivElement>;
}

function StaticOptionList({
  options,
  searchValue,
  onSearchChange,
  value,
  onChange,
  listboxRef,
}: StaticOptionListProps) {
  const source = useStaticOptions(options, searchValue);
  return (
    <OptionList
      source={source}
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      value={value}
      onChange={onChange}
      listboxRef={listboxRef}
    />
  );
}

interface ServerOptionListProps {
  resolver: FilterOptionsResolver;
  /** Names WHICH option source `resolver` currently is; keys its query. */
  optionsKey: string;
  /** Already debounced and trimmed by `OptionsValueInput`. */
  search: string;
  /** The immediate input text — `search` is its settled form. */
  searchValue: string;
  onSearchChange: (next: string) => void;
  value: LabeledOption[];
  onChange: (next: LabeledOption[]) => void;
  listboxRef: React.RefObject<HTMLDivElement>;
}

function ServerOptionList({
  resolver,
  optionsKey,
  search,
  searchValue,
  onSearchChange,
  value,
  onChange,
  listboxRef,
}: ServerOptionListProps) {
  const source = useServerOptions(resolver, optionsKey, search, searchValue);
  return (
    <OptionList
      source={source}
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      value={value}
      onChange={onChange}
      listboxRef={listboxRef}
    />
  );
}

/**
 * The next selection after toggling ``option``: removed if already selected
 * (matched by wire ``value``), appended — label and all — if not.
 */
function toggleOption(
  current: LabeledOption[],
  option: LabeledOption,
): LabeledOption[] {
  return current.some((selected) => selected.value === option.value)
    ? current.filter((selected) => selected.value !== option.value)
    : [...current, option];
}

interface OptionListboxProps {
  options: LabeledOption[];
  value: LabeledOption[];
  onToggle: (option: LabeledOption) => void;
  /** Threaded down from `OptionsValueInput` so ArrowDown in the search input
   * can focus this element directly (see the container's `onKeyDown`). */
  listboxRef: React.RefObject<HTMLDivElement>;
  /** Whether the server capped the result set. */
  truncated: boolean;
  /** Server rows only (pinned selections excluded) — what the cap applied to. */
  resultCount: number;
}

/**
 * Renders the option rows, virtualized: only the rows within (and just
 * outside) the scroll viewport are mounted, so a large option list stays cheap
 * to render, scroll, and interact with. The scroll container is a
 * ``role="listbox"`` div and each row a ``role="option"`` button, with a
 * ``role="presentation"`` sizer between them so the listbox still owns its
 * options in the accessibility tree; because most options are absent from the
 * DOM, each rendered option carries explicit ``aria-setsize`` /
 * ``aria-posinset`` so assistive tech still reports the true position and
 * total (which it would otherwise infer from sibling count).
 *
 * Keyboard model: the listbox itself is the single tab stop (row buttons are
 * ``tabIndex={-1}``). Arrow keys move an active row — reported through
 * ``aria-activedescendant``, scrolled into view via the virtualizer so it is
 * mounted before assistive tech looks it up — Home/End jump to the edges, and
 * Enter or Space toggles the active row.
 */
function OptionListbox({
  options,
  value,
  onToggle,
  listboxRef,
  truncated,
  resultCount,
}: OptionListboxProps) {
  const baseId = React.useId();
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const selectedValues = React.useMemo(
    () => new Set(value.map((option) => option.value)),
    [value],
  );
  const virtualizer = useVirtualizer({
    count: options.length,
    getScrollElement: () => listboxRef.current,
    estimateSize: () => OPTION_ROW_HEIGHT,
    overscan: OPTION_OVERSCAN,
  });

  // Clamp rather than reset when the list shrinks (e.g. the search narrows),
  // so the active row stays as close as possible to where the user left it.
  const active = Math.min(activeIndex, options.length - 1);

  const moveActive = (next: number) => {
    setActiveIndex(next);
    virtualizer.scrollToIndex(next);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        moveActive(Math.min(active + 1, options.length - 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        moveActive(Math.max(active - 1, 0));
        break;
      case "Home":
        event.preventDefault();
        moveActive(0);
        break;
      case "End":
        event.preventDefault();
        moveActive(options.length - 1);
        break;
      case "Enter":
      case " ":
        if (active >= 0) {
          // preventDefault stops Space from scrolling the list and Enter
          // from submitting the enclosing filter form.
          event.preventDefault();
          onToggle(options[active]);
        }
        break;
    }
  };

  return (
    <div
      ref={listboxRef}
      className={styles.list}
      role="listbox"
      aria-label="Filterable values"
      aria-multiselectable="true"
      tabIndex={0}
      aria-activedescendant={
        active >= 0 ? `${baseId}-option-${active}` : undefined
      }
      onKeyDown={handleKeyDown}
      // React's onFocus fires for a focused descendant too, and pressing the mouse on a row
      // focuses that row. Answering with a scroll to the first row would pull the row out from
      // under the pointer before the mouse is released, and no click would reach any row; a
      // reader who has scrolled down finds that the first click on an option does nothing.
      // Only the listbox taking focus itself (Tab, or ArrowDown from the search input) seeds
      // an active row; a row click seeds it below.
      onFocus={(event) => {
        if (
          event.target === event.currentTarget &&
          active < 0 &&
          options.length > 0
        ) {
          moveActive(0);
        }
      }}
    >
      <div
        className={styles.sizer}
        role="presentation"
        style={{ height: `${virtualizer.getTotalSize()}px` }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const option = options[virtualRow.index];
          const isSelected = selectedValues.has(option.value);
          return (
            <button
              key={option.value}
              id={`${baseId}-option-${virtualRow.index}`}
              type="button"
              role="option"
              tabIndex={-1}
              aria-selected={isSelected}
              aria-setsize={options.length}
              aria-posinset={virtualRow.index + 1}
              className={classNames(styles.row, {
                [styles.rowActive]: virtualRow.index === active,
              })}
              style={{
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
              onClick={() => {
                setActiveIndex(virtualRow.index);
                onToggle(option);
              }}
            >
              <Checkbox
                size="small"
                checked={isSelected}
                tabIndex={-1}
                disableRipple
                className={styles.checkbox}
              />
              <span className={styles.rowLabel}>{option.label}</span>
            </button>
          );
        })}
      </div>
      {/* Sticky to the bottom of the SCROLL viewport, and `role="presentation"`
          so a non-option node can't disturb the listbox's ownership of its
          options. */}
      {truncated && (
        <div className={styles.truncationHint} role="presentation">
          <span role="status" aria-live="polite">
            Showing the first {resultCount} matches — refine your search to
            narrow.
          </span>
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className={styles.status} role="status" aria-live="polite">
      No matching options
    </div>
  );
}

function ErrorState() {
  return (
    <div className={classNames(styles.status, styles.error)} role="alert">
      Couldn’t load options. Reopen the menu to retry.
    </div>
  );
}
